import React, { useState, useEffect, useCallback } from 'react';
import { 
  Table, 
  Card, 
  Button, 
  InputNumber, 
  Form, 
  message,
  Space,
  Popconfirm,
  Typography,
  Input
} from 'antd';
import { SaveOutlined, RollbackOutlined, SearchOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { Pack, packService, getPackPrice, PackPrice, convertDataValue, getPackAgencyPrice } from '../../services/packService';
import { privateDecrypt } from 'crypto';
import { FilterConfig, TableFilter } from 'components/TableFilter';
import ReactCountryFlag from 'react-country-flag';
import getCountryISO2 from 'country-iso-3-to-2';

const { Title } = Typography;

const UpdatePacksPrices: React.FC = () => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [packs, setPacks] = useState<Pack[]>([]);
  const [modifiedPrices, setModifiedPrices] = useState<Record<number, number>>({});
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });
  const [filters, setFilters] = useState<any>({});

   const [sortBy, setSortBy] = useState<string | undefined>(undefined);
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc' | undefined>(undefined);

  useEffect(() => {
    fetchPacks(pagination.current, pagination.pageSize, filters, sortBy, sortOrder);
  }, [pagination.current, pagination.pageSize, filters, sortBy, sortOrder]);

  const mapFiltersToBackend = (filters: any) => {
    return {
      name: filters.search,
    };
  };


  const fetchPacks = async (
      page = 1,
      pageSize = 10,
      appliedFilters = {},
      sortField?: string,
      sortDirection?: 'asc' | 'desc'
    ) => {
      try {
        setLoading(true);

        // Construire le paramètre 'sort' compatible Spring Data JPA
        const sortParam = sortField ? `${sortField},${sortDirection ?? 'asc'}` : undefined;

        const response = await packService.getAllAdmin({
          page: page - 1,
          size: pageSize,
          filters: mapFiltersToBackend(appliedFilters),
          sort: sortParam, // <-- ici on envoie le sort correctement
        });

        setPacks(response.data.content);

        setPagination(prev => ({
          ...prev,
          current: page,
          pageSize,
          total: response.data.totalElements,
        }));

        console.log('Filtres backend:', mapFiltersToBackend(appliedFilters));

        // Initialiser les valeurs du formulaire
        const initialValues: Record<string, any> = {};
        response.data.content.forEach((pack: Pack) => {
          initialValues[`price_${pack.id}`] = getPackPrice(pack);
          initialValues[`agencyPrice_${pack.id}`] = getPackAgencyPrice(pack);
        });
        form.setFieldsValue(initialValues);
      } catch (error) {
        message.error(t('admin.updatePacksPrices.loadError'));
      } finally {
        setLoading(false);
      }
    };


  const handlePriceChange = (packId: number, newPrice: number) => {
    setModifiedPrices({
      ...modifiedPrices,
      [`sabily_${packId}`]: newPrice
    });
  };

  const handleAgencyPriceChange = (packId: number, newAgencyPrice: number) => {
    setModifiedPrices({
      ...modifiedPrices,
      [`agency_${packId}`]: newAgencyPrice
    });
  };

  const updatePackPrices = async (pack, priceEUR) => {
    const sabilyKey = `sabily_${pack.id}`;
    const agencyKey = `agency_${pack.id}`;

    // 1 — Sabily d'abord (si modifié)
    if (modifiedPrices[sabilyKey] !== undefined) {
      await packService.updatePrice(
        pack.id,
        priceEUR.id,
        modifiedPrices[sabilyKey],
        true
      );
    }

    // 2 — puis Agency (si modifié)
    if (modifiedPrices[agencyKey] !== undefined) {
      await packService.updatePrice(
        pack.id,
        priceEUR.id,
        modifiedPrices[agencyKey],
        false
      );
    }
  };

  const saveAllChanges = async () => {
    try {
      setSaving(true);

      const promises: Promise<any>[] = [];

      packs.forEach(pack => {
        const priceEUR = pack.prices?.find(p => p.currency.code === 'EUR');
        if (!priceEUR) return;

        promises.push(updatePackPrices(pack, priceEUR));
      });

      await Promise.all(promises);

      message.success(t('admin.updatePacksPrices.updateSuccess'));
      setModifiedPrices({});
      fetchPacks(pagination.current, pagination.pageSize, filters);

    } catch (error) {
      message.error(t('admin.updatePacksPrices.updateError'));
    } finally {
      setSaving(false);
    }
  };

  const cancelChanges = () => {
    // Reset to original values
    const initialValues = {};
    packs.forEach(pack => {
      initialValues[`price_${pack.id}`] = getPackPrice(pack);
      initialValues[`agencyPrice_${pack.id}`] = getPackAgencyPrice(pack);
    });
    form.setFieldsValue(initialValues);
    setModifiedPrices({});
    message.info(t('admin.updatePacksPrices.changesCancelled'));
  };

  const isModified = Object.keys(modifiedPrices).length > 0;

  const columns = [
    {
      title: t('common.name'),
      dataIndex: 'name',
      key: 'name',
      width: 600, 
      ellipsis: false,
      sorter: true,
       sortDirections: ['ascend' as const, 'descend' as const],
         render: (name: string, record: Pack) => (
          <>
            <div
              style={{
                wordBreak: 'break-word',
                whiteSpace: 'normal',
                fontWeight: 500,
                maxWidth: 250,
                overflowWrap: 'break-word',
              }}
            >
              {name}
            </div>
      
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',   // ✅ saut de ligne automatique
                gap: '4px',
                marginTop: '4px',
              }}
            >
              {record.countries?.map(country => (
                <ReactCountryFlag
                  key={country.code}
                  countryCode={getCountryISO2(country.code)}
                  svg
                  style={{
                    width: '1.5em',
                    height: '1.5em',
                    borderRadius: '50%',
                  }}
                />
              ))}
            </div>
          </>
           ),
    },
    {
      title: t('common.duration'),
      dataIndex: 'validityDuration',
      key: 'validityDuration',
      // filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      //   <div style={{ padding: 8 }}>
      //     <Input
      //       placeholder="Rechercher durée"
      //       value={selectedKeys[0]}
      //       onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
      //       onPressEnter={() => confirm()}
      //       style={{ marginBottom: 8, display: 'block' }}
      //     />
      //     <Space>
      //       <Button
      //         type="primary"
      //         onClick={() => confirm()}
      //         icon={<SearchOutlined />}
      //         size="small"
      //       >
      //         Rechercher
      //       </Button>
      //       <Button onClick={() => clearFilters()} size="small">
      //         Réinitialiser
      //       </Button>
      //     </Space>
      //   </div>
      // ),
      // filterIcon: (filtered) => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
      // onFilter: (value, record) => {
      //   const duration = `${record.validityDuration} ${record.validityDurationUnit === 'days' ? 'jours' : record.validityDurationUnit === 'months' ? 'mois' : 'ans'}`;
      //   return duration.toLowerCase().includes(value.toLowerCase());
      // },
      render: (duration, record) => `${duration} ${record.validityDurationUnit === 'days' ? t('common.days') : record.validityDurationUnit === 'months' ? t('common.months') : t('common.years')}`
    },
    {
      title: 'Data',
      dataIndex: 'dataValue',
      key: 'dataValue',
  
      // filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      //   <div style={{ padding: 8 }}>
      //     <Input
      //       placeholder="Rechercher data"
      //       value={selectedKeys[0]}
      //       onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
      //       onPressEnter={() => confirm()}
      //       style={{ marginBottom: 8, display: 'block' }}
      //     />
      //     <Space>
      //       <Button
      //         type="primary"
      //         onClick={() => confirm()}
      //         icon={<SearchOutlined />}
      //         size="small"
      //       >
      //         Rechercher
      //       </Button>
      //       <Button onClick={() => clearFilters()} size="small">
      //         Réinitialiser
      //       </Button>
      //     </Space>
      //   </div>
      // ),
      // filterIcon: (filtered) => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
      // onFilter: (value, record) => {
      //   const dataText = `${convertDataValue(record).value} ${convertDataValue(record).unit}`;
      //   return dataText.toLowerCase().includes(value.toLowerCase());
      // },
      render: (data, record) => {
        var xx = convertDataValue(record);
        return `${xx.value} ${xx.unit}`;
      }
    },
    {
      title: t('admin.updatePacksPrices.currentPrice'),
      dataIndex: 'prices.sabilyAmount',
      key: 'price',
      sorter:true,
      sortDirections: ['ascend' as const, 'descend' as const],
      // dataIndex: 'prices',
      // key: 'price',
      // sorter: (a, b) => (getPackPrice(a) || 0) - (getPackPrice(b) || 0),
      // filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      //   <div style={{ padding: 8 }}>
      //     <Input
      //       placeholder="Rechercher prix"
      //       value={selectedKeys[0]}
      //       onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
      //       onPressEnter={() => confirm()}
      //       style={{ marginBottom: 8, display: 'block' }}
      //     />
      //     <Space>
      //       <Button
      //         type="primary"
      //         onClick={() => confirm()}
      //         icon={<SearchOutlined />}
      //         size="small"
      //       >
      //         Rechercher
      //       </Button>
      //       <Button onClick={() => clearFilters()} size="small">
      //         Réinitialiser
      //       </Button>
      //     </Space>
      //   </div>
      // ),
      // filterIcon: (filtered) => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
      // onFilter: (value, record) => {
      //   const price = `${getPackPrice(record)?.toFixed(2) ?? '-'} €`;
      //   return price.toLowerCase().includes(value.toLowerCase());
      // },
      render: (prices, record) => `${getPackPrice(record)?.toFixed(2) ?? '-'} €`
    },
    {
      title: t('admin.updatePacksPrices.newPrice'),
      key: 'newPrice',
      render: (_, record) => (
        <Form.Item
          name={`price_${record.id}`}
          noStyle
        >
          <InputNumber
            min={0}
            step={0.01}
            addonAfter="€"
            style={{ width: '100%' }}
            onChange={(value) => handlePriceChange(record.id, value)}
          />
        </Form.Item>
      ),
    },
    {
      title: t('admin.updatePacksPrices.currentAgencyPrice'),
      dataIndex: 'prices',
      key: 'agencyAmount',
      sorter: (a, b) => {
        const pa = getPackAgencyPrice(a);
        const pb = getPackAgencyPrice(b);
        return (pa ?? -1) - (pb ?? -1);
      },
      // filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      //   <div style={{ padding: 8 }}>
      //     <Input
      //       placeholder="Rechercher prix"
      //       value={selectedKeys[0]}
      //       onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
      //       onPressEnter={() => confirm()}
      //       style={{ marginBottom: 8, display: 'block' }}
      //     />
      //     <Space>
      //       <Button
      //         type="primary"
      //         onClick={() => confirm()}
      //         icon={<SearchOutlined />}
      //         size="small"
      //       >
      //         Rechercher
      //       </Button>
      //       <Button onClick={() => clearFilters()} size="small">
      //         Réinitialiser
      //       </Button>
      //     </Space>
      //   </div>
      // ),
      // filterIcon: (filtered) => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
      // onFilter: (value, record) => {
      //   const price = `${getPackAgencyPrice(record)?.toFixed(2) ?? '-'} €`;
      //   return price.toLowerCase().includes(value.toLowerCase());
      // },
      render: (_, record) => {
        const price = getPackAgencyPrice(record);
        return price !== null ? `${price.toFixed(2)} €` : '-';
      }
    },
    {
      title: t('admin.updatePacksPrices.newAgencyPrice'),
      key: 'newAgencyPrice',
      render: (_, record) => (
        <Form.Item
          name={`agencyPrice_${record.id}`}
          noStyle
        >
          <InputNumber
            min={0}
            step={0.01}
            addonAfter="€"
            style={{ width: '100%' }}
            onChange={(value) => handleAgencyPriceChange(record.id, value)}
          />
        </Form.Item>
      ),    
    },
    {
      title: t('admin.updatePacksPrices.variation'),
      key: 'variation',
    
      // filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      //   <div style={{ padding: 8 }}>
      //     <Input
      //       placeholder="Rechercher variation"
      //       value={selectedKeys[0]}
      //       onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
      //       onPressEnter={() => confirm()}
      //       style={{ marginBottom: 8, display: 'block' }}
      //     />
      //     <Space>
      //       <Button
      //         type="primary"
      //         onClick={() => confirm()}
      //         icon={<SearchOutlined />}
      //         size="small"
      //       >
      //         Rechercher
      //       </Button>
      //       <Button onClick={() => clearFilters()} size="small">
      //         Réinitialiser
      //       </Button>
      //     </Space>
      //   </div>
      // ),
      // filterIcon: (filtered) => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
      // onFilter: (value, record) => {
      //   const newPrice = modifiedPrices[record.id];
      //   if (newPrice === undefined) return '-'.includes(value.toLowerCase());
        
      //   const currentPrice = getPackPrice(record);
      //   const variation = ((newPrice - currentPrice) / currentPrice) * 100;
      //   const variationText = `${variation > 0 ? '+' : ''}${variation?.toFixed(2)}%`;
        
      //   return variationText.toLowerCase().includes(value.toLowerCase());
      // },
      render: (_, record) => {
        const newPrice = modifiedPrices[record.id];
        if (newPrice === undefined) return '-';
        
        const currentPrice = getPackPrice(record);
        const variation = ((newPrice - currentPrice) / currentPrice) * 100;
        const color = variation > 0 ? 'green' : variation < 0 ? 'red' : '';
        
        return (
          <span style={{ color }}>
            {variation > 0 ? '+' : ''}{variation?.toFixed(2)}%
          </span>
        );
      }
    }
  ];

  const filterConfigs: FilterConfig[] = [
    {
      key: 'search',
      label: t('common.search'),
      type: 'text',
      placeholder: t('filters.searchByName'),
      width: 8,
    }
  ];

  const handleServerFilterChange = useCallback((newFilters: any) => {
    console.log('Filtres envoyés:', newFilters);
    setPagination(prev => ({
      ...prev,
      current: 1,
    }));
    setFilters(newFilters);
  }, []);

  const handleClearFilters = useCallback(() => {
    console.log('Clear filters appelé');
    setPagination(prev => ({ ...prev, current: 1 }));
    setFilters({});
  }, []);

  return (
    <>
      <TableFilter
        filters={filterConfigs}
        onFilterChange={handleServerFilterChange}
        onClearFilters={handleClearFilters}
      />
      
      <Card
        title={<Title level={4}>{t('admin.updatePacksPrices.pageTitle')}</Title>}
        extra={
          <Space>
            <Button 
              type="default" 
              icon={<RollbackOutlined />}
              onClick={() => window.history.back()}
            >
              {t('common.back')}
            </Button>
            <Popconfirm
              title={t('admin.updatePacksPrices.confirmCancel')}
              onConfirm={cancelChanges}
              okText={t('common.yes')}
              cancelText={t('common.no')}
              disabled={!isModified}
            >
              <Button 
                danger 
                disabled={!isModified}
              >
                {t('admin.updatePacksPrices.cancelChangesBtn')}
              </Button>
            </Popconfirm>
            <Popconfirm
              title={t('admin.updatePacksPrices.confirmSave')}
              onConfirm={saveAllChanges}
              okText={t('common.yes')}
              cancelText={t('common.no')}
              disabled={!isModified}
            >
              <Button 
                type="primary" 
                icon={<SaveOutlined />}
                loading={saving}
                disabled={!isModified}
              >
                {t('admin.updatePacksPrices.saveChangesBtn')}
              </Button>
            </Popconfirm> 
          </Space>
        }
      >
        <p>{t('admin.updatePacksPrices.description')}</p>
        
        <Form form={form}>
          {/* <Table
            dataSource={packs}
            columns={columns}
            rowKey="id"
            loading={loading}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} packs`,
              pageSizeOptions: ['10', '20', '50', '100'],
              defaultPageSize: 20,
              onChange: (page, pageSize) => {
                setPagination(prev => ({
                  ...prev,
                  current: page,
                  pageSize: pageSize
                }));
              }
            }}
            scroll={{ x: 'max-content' }}
            size="middle"
          /> */}


          <Table
              dataSource={packs}
              columns={columns.map(col => {
                if (!col.sorter) return col;

                let backendKey = col.key;
                if (col.key === 'price') backendKey = 'prices.sabilyAmount';
                if (col.key === 'agencyAmount') backendKey = 'prices.agencyAmount';
                if (col.key === 'name') backendKey = 'name';

                return {
                  ...col,
                  sortOrder:
                    sortBy === backendKey
                      ? sortOrder === 'asc'
                        ? 'ascend'
                        : 'descend'
                      : undefined,
                };
              })}
              rowKey="id"
              loading={loading}
              pagination={{
                current: pagination.current,
                pageSize: pagination.pageSize,
                total: pagination.total,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} packs`,
                pageSizeOptions: ['10', '20', '50', '100'],
                defaultPageSize: 20,
              }}
              onChange={(pag, _filters, sorter: any) => {
                // Mapper les champs frontend vers backend
                let field = sorter.field;
                if (field === 'price') field = 'prices.sabilyAmount';
                if (field === 'agencyAmount') field = 'prices.agencyAmount';
                if (field === 'name') field = 'name';

                let order: 'asc' | 'desc' = 'asc';
                if (sorter.order === 'descend') order = 'desc';

                const isPageSizeChange = pag.pageSize !== pagination.pageSize;

                setSortBy(field);
                setSortOrder(order);
                setPagination(prev => ({
                  ...prev,
                  current: isPageSizeChange ? 1 : pag.current || 1,
                  pageSize: pag.pageSize || prev.pageSize,
                }));

                fetchPacks(
                  isPageSizeChange ? 1 : pag.current || 1,
                  pag.pageSize || pagination.pageSize,
                  filters,
                  field,
                  order
                );
              }}
              scroll={{ x: 'max-content' }}
              size="middle"
            />

        </Form>
        
        {isModified && (
          <div style={{ marginTop: 16, textAlign: 'right' }}>
            <Space>
              <Button onClick={cancelChanges}>
                Annuler
              </Button>
              <Button 
                type="primary" 
                icon={<SaveOutlined />}
                onClick={saveAllChanges}
                loading={saving}
              >
                Enregistrer les modifications
              </Button>
            </Space>
          </div>
        )}
      </Card>
    </>
  );
};

export default UpdatePacksPrices;